import { BLOCKS } from '@contentful/rich-text-types';
import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import React, { useRef, useState } from 'react';

import ContentfulImage from '../components/Common/ContentfulImage';
import { PrimaryLink } from '../components/Common/Links';
import EmbeddedMarketoForm from '../components/Common/MarketoForm/EmbeddedMarketoForm';
import RichTextConverter from '../components/Common/RichText/RichTextConverter';
import { scrollToFormTop, smoothScrollToRef } from '../components/Common/ScrollTo';
import { AutoPauseVideo } from '../components/Generic/AutoPauseVideo';
import Layout from '../components/Layouts/Layout';
import MarketingSeo from '../components/MarketingSeo';
import PageSection from '../components/PageSections/PageSection';
import { sectionBuilder } from '../tools/sectionBuilder';
import * as styles from './paidSearch.module.scss';
import * as standardStyles from './standardPage.module.scss';

const PaidSearchPage = ({ data, pageContext }) => {
  let page = {};
  if (data.page) page = data.page;

  const [formSuccess, setFormSuccess] = useState(false);
  const formRef = useRef();
  const onCtaClick = () => {
    smoothScrollToRef(formRef, 'smooth', 0);
  };

  const options = {
    renderNode: {
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const { file, title, description, gatsbyImageData } = node?.data?.target || {};

        return (
          <figure className={styles.figure}>
            {gatsbyImageData && <GatsbyImage data-ot-ignore image={gatsbyImageData} alt={title} />}
            {!gatsbyImageData && <img src={file?.url} alt={title || ''} />}
            {description && <figcaption>{description}</figcaption>}
          </figure>
        );
      },
    },
  };

  //This is to direct a specific page section flow but still run through our section builder
  let pageSections = [];
  if (page.threeUpSection) pageSections.push(page.threeUpSection);
  if (page.testimonialSection) pageSections.push(page.testimonialSection);
  if (page.zPatternSection) pageSections.push(page.zPatternSection);
  if (page.logoSection) pageSections.push(page.logoSection);
  if (page.badgeLogoSection) pageSections.push(page.badgeLogoSection);

  const sections = sectionBuilder({ pageSections }, standardStyles, '');
  const formContainerRef = useRef(null);
  const onSuccess = () => {
    scrollToFormTop(formContainerRef);
    setFormSuccess(true);
  };

  let meta = { ...page.pageMetaData, noFollow: 'Yes', noIndex: true };

  return (
    <Layout hideNav={true} displayCta={false} pageMeta={meta} isDach={pageContext.isDach}>
      <PageSection isTopSection className={styles.heading} wide data-analytics-section={'paid-search-header'}>
        <div className={styles.copy}>
          <img alt={page.heading || ''} src={'/images/green-pink-blob-bg.svg'} />
          <div className={styles.copyWrapper}>
            <div>
              <h1 suppressHydrationWarning={true}>{page.heading}</h1>
              <RichTextConverter copy={page.headingIntroCopy} options={options} />
              {page.heroMedia && (
                <ContentfulImage
                  data-analytics-component={'header-image'}
                  className={styles.heroMedia}
                  imageFragment={page.heroMedia}
                />
              )}
              {page.videoUrl && !page.heroMedia && (
                <div data-analytics-component={'header-video'}>
                  <AutoPauseVideo src={page.videoUrl} classes={[styles.videoWrap]} />
                </div>
              )}
            </div>
          </div>
        </div>
        {page.marketoFormId && (
          <div className={styles.formWrapper} ref={formRef} data-analytics-section={'form-wrapper'}>
            <div className={styles.form} ref={formContainerRef}>
              {!formSuccess && <EmbeddedMarketoForm onSuccess={onSuccess} formId={page.marketoFormId} />}
              {/*<MarketoForm formId={page.marketoFormId} onSuccess={() => setFormSuccess(true)} />*/}
              {formSuccess && (
                <div className={'marketo-render-success'} data-analytics-component={'form-success'}>
                  <RichTextConverter copy={page.successLede} />
                </div>
              )}
            </div>
          </div>
        )}
      </PageSection>
      {sections}
      {sections.length > 1 && (
        <PageSection className={styles.ctaFooter} data-analytics-section={'cta-footer'}>
          <div className={styles.ctaCenter}>
            <RichTextConverter className={styles.ctaCenter} copy={page.footerCtaCopy} />{' '}
            <div>
              <PrimaryLink label={'Get a Demo'} isButton={true} onClick={onCtaClick} />
            </div>
          </div>
        </PageSection>
      )}
    </Layout>
  );
};

export default PaidSearchPage;

export const Head = ({ data: { page }, location }) => {
  let meta = { ...page.pageMetaData, noFollow: 'Yes', noIndex: true };
  return <MarketingSeo meta={meta} slug={location.pathname} />;
};

export const query = graphql`
  query PaidSearchQuery($slug: String!) {
    page: contentfulPagePaidSearch(slug: { eq: $slug }) {
      ...PaidSearch
    }
  }
`;
